/**
 * @fileoverview Dropdown navigation item component for the header.
 */

import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

import '../scss/page/_dropdown-nav-item.scss';

/**
 * Checks if a URL is external (starts with http:// or https://).
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} True if external.
 */
const isExternalLink = (url) => /^https?:\/\//.test(url);

/**
 * A navigation item with a hover-activated dropdown menu.
 * Supports both internal routes (using react-router Link) and
 * external URLs (using anchor tags).
 *
 * @param {Object} props - Component props.
 * @param {string} props.label - The label for the nav item.
 * @param {string} [props.href] - Optional external link destination.
 * @param {Array} props.items - Array of dropdown items with `label` and `href` properties.
 */
const DropdownNavItem = ({ label, href, items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => setIsOpen(true);
  const handleMouseLeave = () => setIsOpen(false);

  const renderParentLink = () => {
    if (!href) {
      return (
        <span className='nav-item nav-link dropdown-toggle'>
          { label }
        </span>
      );
    }

    if (isExternalLink(href)) {
      return (
        <a className='nav-item nav-link dropdown-toggle' href={ href }>
          { label }
        </a>
      );
    }

    return (
      <Link className='nav-item nav-link dropdown-toggle' to={ href }>
        { label }
      </Link>
    );
  };

  const renderDropdownItem = (item) => {
    if (isExternalLink(item.href)) {
      return (
        <a
          className='dropdown-item'
          href={ item.href }
          key={ item.href }
          onClick={ () => setIsOpen(false) }
        >
          { item.label }
        </a>
      );
    }

    return (
      <Link
        className='dropdown-item'
        key={ item.href }
        onClick={ () => setIsOpen(false) }
        to={ item.href }
      >
        { item.label }
      </Link>
    );
  };

  return (
    <div
      className='dropdown-nav-item'
      onMouseEnter={ handleMouseEnter }
      onMouseLeave={ handleMouseLeave }
      ref={ dropdownRef }
    >
      { renderParentLink() }

      { isOpen && items && items.length > 0 && (
        <div className='dropdown-menu show'>
          { items.map(renderDropdownItem) }
        </div>
      ) }
    </div>
  );
};

DropdownNavItem.propTypes = {
  href: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  label: PropTypes.string.isRequired,
};

DropdownNavItem.defaultProps = {
  href: null,
};

export default DropdownNavItem;
